import React from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  GitMerge,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Flag,
  ChevronRight,
  Mail,
  Phone,
  Building
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DuplicateSet, MatchType, DuplicateSetStatus } from '@/types/duplicates';
import { cn } from '@/lib/utils';

interface DuplicateSetCardProps {
  set: DuplicateSet;
  onClick: (set: DuplicateSet) => void;
  onStatusChange?: (setId: string, status: DuplicateSetStatus) => void;
}

const matchTypeLabels: Record<MatchType, string> = {
  'EXACT_EMAIL': 'Email Match',
  'EXACT_PHONE': 'Phone Match',
  'FUZZY_NAME_COMPANY': 'Name/Company',
  'MULTIPLE_FIELDS': 'Multiple Fields'
};

const matchTypeIcons: Record<MatchType, React.ReactNode> = {
  'EXACT_EMAIL': <Mail className="h-3 w-3" />,
  'EXACT_PHONE': <Phone className="h-3 w-3" />,
  'FUZZY_NAME_COMPANY': <Building className="h-3 w-3" />,
  'MULTIPLE_FIELDS': <GitMerge className="h-3 w-3" />
};

const statusConfig: Record<DuplicateSetStatus, {
  label: string;
  icon: React.ReactNode;
  bgColor: string;
  textColor: string;
  borderColor: string;
}> = {
  'PENDING': {
    label: 'Pending Review',
    icon: <Clock className="h-3 w-3" />,
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-700',
    borderColor: 'border-yellow-200'
  },
  'REVIEWING': {
    label: 'Under Review',
    icon: <AlertTriangle className="h-3 w-3" />,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200'
  },
  'MERGED': {
    label: 'Merged',
    icon: <CheckCircle className="h-3 w-3" />,
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200'
  },
  'DISMISSED': {
    label: 'Dismissed',
    icon: <XCircle className="h-3 w-3" />,
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-600',
    borderColor: 'border-gray-200'
  },
  'FLAGGED': {
    label: 'Flagged',
    icon: <Flag className="h-3 w-3" />,
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200'
  }
};

export const DuplicateSetCard: React.FC<DuplicateSetCardProps> = ({
  set,
  onClick,
  onStatusChange
}) => {
  const statusInfo = statusConfig[set.status];
  const memberCount = set.members.length;
  const primaryMember = set.members.find(m => m.isPrimary);

  const getScoreColor = (score: number) => {
    if (score >= 0.9) return 'text-red-600 bg-red-100';
    if (score >= 0.7) return 'text-orange-600 bg-orange-100';
    return 'text-yellow-600 bg-yellow-100';
  };

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <Card
        className={cn(
          "p-4 cursor-pointer transition-all duration-200 hover:shadow-md border-l-4",
          statusInfo.borderColor
        )}
        onClick={() => onClick(set)}
      >
        <div className="flex items-start justify-between">
          {/* Left section */}
          <div className="flex-1">
            {/* Header with member count and match type */}
            <div className="flex items-center space-x-3 mb-3">
              <div className="flex items-center space-x-2">
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  statusInfo.bgColor
                )}>
                  <Users className={cn("h-4 w-4", statusInfo.textColor)} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {memberCount} Duplicate Lead{memberCount > 1 ? 's' : ''}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Set #{set.id.slice(0, 8)}
                  </p>
                </div>
              </div>
            </div>

            {/* Match info */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {/* Match type badge */}
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                {matchTypeIcons[set.matchType]}
                <span className="ml-1">{matchTypeLabels[set.matchType]}</span>
              </span>

              {/* Score badge */}
              <span className={cn(
                "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                getScoreColor(set.matchScore)
              )}>
                {Math.round(set.matchScore * 100)}% Match
              </span>

              {/* Status badge */}
              <span className={cn(
                "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
                statusInfo.bgColor,
                statusInfo.textColor,
                statusInfo.borderColor
              )}>
                {statusInfo.icon}
                <span className="ml-1">{statusInfo.label}</span>
              </span>
            </div>

            {/* Member preview */}
            <div className="space-y-1">
              {set.members.slice(0, 3).map((member) => (
                <div key={member.id} className="flex items-center space-x-2 text-sm">
                  <span className={cn(
                    "w-2 h-2 rounded-full",
                    member.isPrimary ? "bg-blue-500" : "bg-gray-300"
                  )} />
                  <span className="text-gray-900 font-medium truncate max-w-[150px]">
                    {member.lead.name}
                  </span>
                  <span className="text-gray-400">-</span>
                  <span className="text-gray-600 truncate max-w-[120px]">
                    {member.lead.company}
                  </span>
                  {member.isPrimary && (
                    <span className="text-xs text-blue-600 font-medium">(Primary)</span>
                  )}
                </div>
              ))}
              {set.members.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{set.members.length - 3} more
                </span>
              )}
            </div>

            {/* Matching fields */}
            <div className="mt-3 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Matched on: {set.matchFields.join(', ')}
              </span>
            </div>
          </div>

          {/* Right section - actions */}
          <div className="flex flex-col items-end space-y-2">
            <ChevronRight className="h-5 w-5 text-gray-400" />

            {/* Quick actions */}
            {set.status === 'PENDING' && onStatusChange && (
              <div className="flex space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(set.id, 'REVIEWING');
                  }}
                  className="h-6 px-2 text-xs"
                >
                  Review
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusChange(set.id, 'DISMISSED');
                  }}
                  className="h-6 px-2 text-xs text-gray-500"
                >
                  Dismiss
                </Button>
              </div>
            )}

            {/* Resolved info */}
            {set.resolvedAt && set.resolvedBy && (
              <div className="text-right">
                <p className="text-xs text-gray-500">
                  {new Date(set.resolvedAt).toLocaleDateString()}
                </p>
                <p className="text-xs text-gray-400">
                  by {set.resolvedBy.name}
                </p>
              </div>
            )}

            {/* Created date for pending */}
            {!set.resolvedAt && (
              <p className="text-xs text-gray-400">
                {new Date(set.createdAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </Card>
    </motion.div>
  );
};
